import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, ButtonSelect, Icon, Menu } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  setPersonalizationViewOption,
  setRowsFiltersForCerts,
  setTriggerClearBtnForHierarchy,
  updatePersonalizationAll,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  SCREEN_SIZE_TABLET_XSMALL,
  SCREEN_WIDTH_QUERIES,
  VIEW_OPTIONS,
} from '../../../utils/constant';

import RowsFilter from './RowsFilter';
import {
  checkAllPersonalizedRowsExist,
  getActiveFiltersForRows,
  getCheckedFiltersForRows,
  getDefaultRowFilter,
  reMapRowsFilterTree,
  setPersonalizedRows,
} from './utils';

import './RowsFilter.css';

function RowFilterButton({
  showbadgeOnRowButton,
  setShowbadgeOnRowButton,
  rowFilters,
  setRowFilters,
  appliedRowFilters,
  setAppliedRowFilters,
  isButtonKindIcon,
  label,
}) {
  const dispatch = useDispatch();

  const {
    core: { isLoading, campaignType },
    global: { mediaQueryValue, viewRowsList },
    personalization: {
      clearCurrentPersonalization,
      personalizedData,
      viewOption,
    },
    tabs: { tabData },
    viewControls: { triggerClearBtnForHierarchy, groupByOptions },
  } = useSelector((state) => state?.certifications?.details);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;
  const personalizationViewOption = viewOption?.view;
  const personalizedRowsFilters = personalizedData?.result?.info
    ?.defaultView?.[0]
    ? personalizedData.result.info.defaultView[0].rowsFilters
    : undefined;
  const tabletXSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_XSMALL
  );
  const isTabletXSmallWidth = tabletXSmallQuery.value <= mediaQueryValue;

  const shouldShowBadgeOnRowButton = (filters) => {
    const activeFiltersForRows = getActiveFiltersForRows(rowFilters);

    return (
      activeFiltersForRows.length !== filters.length && filters.length !== 0
    );
  };

  const onRowUpdate = () => {
    if (viewRowsList && viewRowsList?.length > 0) {
      const tempRowsTree = reMapRowsFilterTree(
        getDefaultRowFilter(campaignType),
        viewRowsList,
        campaignType
      );

      if (
        personalizedRowsFilters &&
        personalizedRowsFilters?.length > 0 &&
        personalizationViewOption === VIEW_OPTIONS.MY_PERSONALIZED_VIEW
      ) {
        if (
          checkAllPersonalizedRowsExist(
            isEOCampaign,
            viewRowsList,
            personalizedRowsFilters
          )
        ) {
          setPersonalizedRows(tempRowsTree, personalizedRowsFilters);
          dispatch(updatePersonalizationAll({ rows: true }));
        } else {
          dispatch(
            setPersonalizationViewOption({
              view: VIEW_OPTIONS.DEFAULT_VIEW,
              applied: false,
            })
          );
          dispatch(updatePersonalizationAll({ rows: false }));
        }
      }

      setRowFilters(tempRowsTree);
      setAppliedRowFilters(getCheckedFiltersForRows(tempRowsTree));
    } else {
      setRowFilters(getDefaultRowFilter(campaignType));
      setAppliedRowFilters(
        getCheckedFiltersForRows(getDefaultRowFilter(campaignType))
      );
    }
  };

  const resetRowFiltersOnUI = () => {
    if (viewRowsList && viewRowsList?.length > 0) {
      const tempRowsTree = reMapRowsFilterTree(
        getDefaultRowFilter(campaignType),
        viewRowsList,
        campaignType
      );

      setRowFilters(tempRowsTree);
      setAppliedRowFilters(getCheckedFiltersForRows(tempRowsTree));
    } else {
      setRowFilters(getDefaultRowFilter(campaignType));
      setAppliedRowFilters(
        getCheckedFiltersForRows(getDefaultRowFilter(campaignType))
      );
    }
  };

  useEffect(() => {
    onRowUpdate();
  }, [viewRowsList, personalizedRowsFilters, personalizationViewOption]);

  useEffect(() => {
    if (clearCurrentPersonalization) {
      onRowUpdate();
    }
  }, [clearCurrentPersonalization]);

  useEffect(() => {
    if (triggerClearBtnForHierarchy) {
      resetRowFiltersOnUI();
      dispatch(setTriggerClearBtnForHierarchy(false));
    }
  }, [triggerClearBtnForHierarchy]);

  useEffect(() => {
    dispatch(setRowsFiltersForCerts(appliedRowFilters));
    setShowbadgeOnRowButton(shouldShowBadgeOnRowButton(appliedRowFilters));
  }, [appliedRowFilters, groupByOptions]);

  return (
    <Menu
      CustomMenu={
        <RowsFilter
          filters={rowFilters}
          appliedRowFilters={appliedRowFilters}
          setAppliedRowFilters={setAppliedRowFilters}
        />
      }
      placement={isTabletXSmallWidth ? 'bottom-start' : 'bottom-end'}
      fallbackPlacements={['bottom', 'bottom-start']}
      trigger={
        <ButtonSelect
          kind={isButtonKindIcon ? 'rectangle' : 'icon'}
          label={label}
          size='medium'
          BadgeComp={
            showbadgeOnRowButton ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
          prefixIcon={<Icon kind='Rows' />}
          isDisabled={activeTabCount === 0 || isLoading}
          isVisualCueEnabled={false}
          className='RowsFilter-button'
        />
      }
      className='RowsFilter'
    />
  );
}

RowFilterButton.propTypes = {
  showbadgeOnRowButton: PropTypes.bool.isRequired,
  setShowbadgeOnRowButton: PropTypes.func.isRequired,
  rowFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setRowFilters: PropTypes.func.isRequired,
  appliedRowFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  setAppliedRowFilters: PropTypes.func.isRequired,
  isButtonKindIcon: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

export default RowFilterButton;
